
import React from 'react';
import { useAuditMonitoring } from '../../hooks/useAuditMonitoring';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Shield, 
  Activity, 
  Users, 
  Clock, 
  AlertTriangle, 
  RefreshCw,
  TrendingUp,
  Database
} from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { format } from 'date-fns';

const AuditMonitorDashboard = () => {
  const { stats, recentEntries, securityAlerts, isLoading, refreshData } = useAuditMonitoring();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const getSecurityLevelBadge = (level: string) => {
    switch (level) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-800">High</Badge>;
      case 'medium':
        return <Badge variant="outline">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Low</Badge>;
      default:
        return <Badge variant="outline">{level}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Audit Monitor Dashboard</h2>
          <p className="text-muted-foreground">Real-time security and performance monitoring</p>
        </div>
        <Button onClick={refreshData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Database className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Accesses</p>
                  <p className="text-xl font-semibold">{stats.totalAccesses}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Unique Users</p>
                  <p className="text-xl font-semibold">{stats.uniqueUsers}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response</p>
                  <p className="text-xl font-semibold">{stats.averageResponseTime.toFixed(0)}ms</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className={`h-5 w-5 ${stats.securityAlerts > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm text-muted-foreground">Security Alerts</p>
                  <p className="text-xl font-semibold">{stats.securityAlerts}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activity">Recent Activity</TabsTrigger>
          <TabsTrigger value="alerts">Security Alerts</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Data Access Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentEntries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No recent activity to display</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentEntries.slice(0, 20).map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{entry.action}</span>
                          {getSecurityLevelBadge(entry.securityLevel)}
                          <span className="text-sm text-muted-foreground">
                            {entry.resourceType}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {entry.userId && <span>User: {entry.userId.slice(0, 8)}... • </span>}
                          {format(entry.timestamp, 'MMM dd, HH:mm:ss')}
                          {entry.performanceMetrics && (
                            <span> • {entry.performanceMetrics.duration.toFixed(0)}ms</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {securityAlerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No security alerts - all systems operating normally</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {securityAlerts.map((alert) => (
                    <div key={alert.id} className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <span className="font-medium">{alert.action}</span>
                          {getSecurityLevelBadge(alert.securityLevel)}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {format(alert.timestamp, 'MMM dd, HH:mm:ss')}
                        </span>
                      </div>
                      {alert.details && (
                        <div className="text-sm text-muted-foreground mt-2">
                          <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
                            {JSON.stringify(alert.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Top Actions (24h)</h4>
                      <div className="space-y-2">
                        {stats.topActions.map((action, index) => (
                          <div key={action.action} className="flex justify-between items-center">
                            <span className="text-sm">{action.action}</span>
                            <Badge variant="outline">{action.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">System Health</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Avg Response Time</span>
                          <Badge variant={stats.averageResponseTime > 1000 ? "destructive" : "secondary"}>
                            {stats.averageResponseTime.toFixed(0)}ms
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Last Activity</span>
                          <span className="text-sm text-muted-foreground">
                            {format(stats.lastActivity, 'HH:mm:ss')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditMonitorDashboard;
